import { useQuery } from '@tanstack/react-query';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import { usePendingSlides } from 'components/StudyDashboard/ProceduresPage/usePendingSlides';
import { useStableProcedureStatistics } from 'components/StudyDashboard/ProceduresPage/useStableProcedureStatistics';
import { flatMap, map, uniq } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { useCasesParams } from 'utils/useCasesParams';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { useProceduresWithQAExperimentResultsOnly } from './useProceduresWithQAExperimentResultsOnly';

export const useCurrentStateCases = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);

  const {
    data: proceduresData,
    isLoading: proceduresLoading,
    isFetching: proceduresFetching,
    error: casesError,
  } = useProceduresWithQAExperimentResultsOnly({
    enabled: !pendingSlidesMode && enabled,
  });
  const {
    mockCasesOfPendingSlides,
    isPendingSlidesLoading,
    totalPendingSlides,
    pendingSlidesError,
    pendingSlidesInPreviousPages,
    pendingCasesInPreviousPages,
    pendingSlidesQuery,
  } = usePendingSlides({ enabled: pendingSlidesMode && enabled });
  const {
    totalCases,
    totalSlides: totalSlidesInCases,
    slidesInPreviousPages: slidesInCasesInPrevPages,
    proceduresInPreviousPages: casesInPrevPages,
  } = useStableProcedureStatistics({ enabled });

  const isLoading = pendingSlidesMode ? isPendingSlidesLoading : proceduresLoading;
  const totalCasesCount = pendingSlidesMode ? totalPendingSlides : totalCases;
  const totalSlidesCount = pendingSlidesMode ? totalPendingSlides : totalSlidesInCases;
  const procedures = pendingSlidesMode ? mockCasesOfPendingSlides : proceduresData?.procedures;
  const slidesInPreviousPages = pendingSlidesMode ? pendingSlidesInPreviousPages : slidesInCasesInPrevPages;
  const casesInPreviousPages = pendingSlidesMode ? pendingCasesInPreviousPages : casesInPrevPages;
  const error = pendingSlidesMode ? pendingSlidesError : casesError;

  const currentPageStains = useMemo(
    () => uniq(flatMap(procedures, ({ slides }) => map(slides, (slide) => slide.stainingType))),
    [procedures]
  );

  const { casesParams } = useCasesParams();
  const stainTypesQueryParams = useMemo(
    () => (pendingSlidesMode ? { ...casesParams, pendingSlidesMode: true } : casesParams),
    [casesParams, pendingSlidesMode]
  );

  const { data: filteredStains } = useQuery({
    queryKey: ['currentStateCasesStainTypes', stainTypesQueryParams],
    queryFn: ({ signal }) => getStainTypeFilteredIds(stainTypesQueryParams, signal),
    enabled,
    placeholderData: currentPageStains,
  });

  const currentStains = filteredStains;

  return {
    isLoading: isLoading && enabled,
    isRefreshing: enabled && !isLoading && (pendingSlidesMode ? pendingSlidesQuery.isFetching : proceduresFetching),
    totalCasesCount,
    totalSlidesCount,
    procedures,
    slidesInPreviousPages,
    casesInPreviousPages,
    currentStains,
    error,
  };
};
